import React, { createContext, useContext, useReducer, useCallback } from 'react';
import errorReportingService from '../services/errorReporting';

// Error context for global error handling
const ErrorContext = createContext();

// Error types
export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTH: 'auth',
  PERMISSION: 'permission',
  VALIDATION: 'validation',
  COMPONENT: 'component',
  CHUNK: 'chunk',
  UNKNOWN: 'unknown'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Initial state
const initialState = {
  errors: [],
  currentError: null,
  isLoading: false,
  retryCount: 0,
  maxRetries: 3,
  errorHistory: []
};

// Action types
const ERROR_ACTIONS = {
  ADD_ERROR: 'ADD_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  CLEAR_ALL_ERRORS: 'CLEAR_ALL_ERRORS',
  SET_LOADING: 'SET_LOADING',
  INCREMENT_RETRY_COUNT: 'INCREMENT_RETRY_COUNT',
  RESET_RETRY_COUNT: 'RESET_RETRY_COUNT',
  ADD_TO_HISTORY: 'ADD_TO_HISTORY',
  CLEAR_HISTORY: 'CLEAR_HISTORY'
};

// Reducer
const errorReducer = (state, action) => {
  switch (action.type) {
    case ERROR_ACTIONS.ADD_ERROR:
      return {
        ...state,
        errors: [...state.errors, action.payload],
        currentError: action.payload
      };
    
    case ERROR_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload),
        currentError: state.currentError?.id === action.payload ? null : state.currentError
      };
    
    case ERROR_ACTIONS.CLEAR_ALL_ERRORS:
      return {
        ...state,
        errors: [],
        currentError: null
      };
    
    case ERROR_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case ERROR_ACTIONS.INCREMENT_RETRY_COUNT:
      return {
        ...state,
        retryCount: state.retryCount + 1
      };
    
    case ERROR_ACTIONS.RESET_RETRY_COUNT:
      return {
        ...state,
        retryCount: 0
      };
    
    case ERROR_ACTIONS.ADD_TO_HISTORY:
      return {
        ...state,
        errorHistory: [...state.errorHistory, action.payload].slice(-100) // Keep last 100 errors
      };
    
    case ERROR_ACTIONS.CLEAR_HISTORY:
      return {
        ...state,
        errorHistory: []
      };
    
    default:
      return state;
  }
};

// Error context provider
export const ErrorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Generate unique error ID
  const generateErrorId = () => {
    return `ctx_err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add error to context
  const addError = useCallback((error, options = {}) => {
    const errorId = generateErrorId();
    const timestamp = new Date().toISOString();
    
    const errorData = {
      id: errorId,
      message: error?.message || 'Unknown error occurred',
      type: options.type || ERROR_TYPES.UNKNOWN,
      severity: options.severity || ERROR_SEVERITY.MEDIUM,
      timestamp,
      stack: error?.stack,
      context: options.context || {},
      userAction: options.userAction || null,
      component: options.component || null,
      retryable: options.retryable !== false,
      maxRetries: options.maxRetries || 3,
      ...options
    };

    // Add to state
    dispatch({
      type: ERROR_ACTIONS.ADD_ERROR,
      payload: errorData
    });

    // Add to history
    dispatch({
      type: ERROR_ACTIONS.ADD_TO_HISTORY,
      payload: errorData
    });

    // Report to error service
    errorReportingService.reportError({
      type: 'context_error',
      error: error,
      errorData,
      source: 'ErrorContext'
    });

    // Add breadcrumb for debugging
    errorReportingService.addBreadcrumb({
      message: `Error added to context: ${errorData.message}`,
      category: 'error',
      level: 'error',
      data: errorData
    });

    return errorId;
  }, []);

  // Clear specific error
  const clearError = useCallback((errorId) => {
    dispatch({
      type: ERROR_ACTIONS.CLEAR_ERROR,
      payload: errorId
    });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    dispatch({
      type: ERROR_ACTIONS.CLEAR_ALL_ERRORS
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((isLoading) => {
    dispatch({
      type: ERROR_ACTIONS.SET_LOADING,
      payload: isLoading
    });
  }, []);

  // Handle retry
  const handleRetry = useCallback(async (errorId, retryCallback) => {
    const error = state.errors.find(err => err.id === errorId);
    
    if (!error || !error.retryable || state.retryCount >= error.maxRetries) {
      return false;
    }

    dispatch({ type: ERROR_ACTIONS.INCREMENT_RETRY_COUNT });

    try {
      if (retryCallback) {
        await retryCallback();
      }
      
      clearError(errorId);
      dispatch({ type: ERROR_ACTIONS.RESET_RETRY_COUNT });
      
      // Add success breadcrumb
      errorReportingService.addBreadcrumb({
        message: `Error retry successful: ${error.message}`,
        category: 'error',
        level: 'info'
      });
      
      return true;
    } catch (retryError) {
      // Add retry failure to context
      addError(retryError, {
        type: error.type,
        context: { originalError: error, retryAttempt: state.retryCount + 1 }
      });
      
      return false;
    }
  }, [state.errors, state.retryCount, clearError, addError]);

  // Handle network errors
  const handleNetworkError = useCallback((error, context = {}) => {
    return addError(error, {
      type: ERROR_TYPES.NETWORK,
      severity: ERROR_SEVERITY.HIGH,
      context: {
        url: context.url,
        method: context.method,
        statusCode: context.statusCode,
        ...context
      },
      retryable: true,
      maxRetries: 3
    });
  }, [addError]);

  // Handle authentication errors
  const handleAuthError = useCallback((error, context = {}) => {
    return addError(error, {
      type: ERROR_TYPES.AUTH,
      severity: ERROR_SEVERITY.HIGH,
      context: {
        loginRequired: true,
        ...context
      },
      retryable: false
    });
  }, [addError]);

  // Handle validation errors
  const handleValidationError = useCallback((error, context = {}) => {
    return addError(error, {
      type: ERROR_TYPES.VALIDATION,
      severity: ERROR_SEVERITY.LOW,
      context: {
        field: context.field,
        value: context.value,
        validationRules: context.validationRules,
        ...context
      },
      retryable: false
    });
  }, [addError]);

  // Handle component errors
  const handleComponentError = useCallback((error, componentName, context = {}) => {
    return addError(error, {
      type: ERROR_TYPES.COMPONENT,
      severity: ERROR_SEVERITY.MEDIUM,
      component: componentName,
      context: {
        props: context.props,
        state: context.state,
        ...context
      },
      retryable: true,
      maxRetries: 2
    });
  }, [addError]);

  // Get error statistics
  const getErrorStats = useCallback(() => {
    const stats = {
      total: state.errorHistory.length,
      byType: {},
      bySeverity: {},
      recent: state.errorHistory.slice(-10)
    };

    state.errorHistory.forEach(error => {
      stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
      stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
    });

    return stats;
  }, [state.errorHistory]);

  // Export error data for debugging
  const exportErrorData = useCallback(() => {
    return {
      currentErrors: state.errors,
      errorHistory: state.errorHistory,
      stats: getErrorStats(),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
  }, [state.errors, state.errorHistory, getErrorStats]);

  const value = {
    // State
    ...state,
    
    // Actions
    addError,
    clearError,
    clearAllErrors,
    setLoading,
    handleRetry,
    
    // Specific error handlers
    handleNetworkError,
    handleAuthError,
    handleValidationError,
    handleComponentError,
    
    // Utilities
    getErrorStats,
    exportErrorData,
    
    // Constants
    ERROR_TYPES,
    ERROR_SEVERITY
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook to use error context
export const useError = () => {
  const context = useContext(ErrorContext);
  
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  
  return context;
};

// Higher-order component for error boundaries
export const withErrorHandling = (Component, errorOptions = {}) => {
  return function WrappedComponent(props) {
    const { addError, clearError } = useError();
    
    const handleError = (error, context) => {
      addError(error, {
        component: Component.name || 'Unknown',
        ...errorOptions,
        ...context
      });
    };

    return <Component {...props} onError={handleError} clearError={clearError} />;
  };
};

export default ErrorContext;
